import type { AppError } from "@/types";

/**
 * Backend error → user-facing copy for the `/multisig/enrollment` endpoints.
 *
 * WORDING SOURCE. Each message below restates one backend condition in the
 * words `docs/sdp/multisig.md` uses for it, so the UI and the spec agree:
 *   - "Enrollment Lifecycle Rules": one enrollment pending per organization,
 *     the 4-day expiry of the invitation and the enrollment, cancellation by
 *     any Owner, and the 24-hour re-proof on resumption.
 *   - "Key Ownership Challenge": the challenge is single-use and valid for
 *     5 minutes, and the wallet signs it with `stellar_signXDR`.
 *   - "Multisig Enrollment / Preconditions": Backend-Only state, the Owner
 *     role for the initiator and for Owner 2, and no unexpected signers on
 *     the account being enrolled.
 * The copy is not final product wording: it needs a content review before
 * release, and any change to these rules in the spec should land here too.
 *
 * The backend (`internal/serve/httphandler/multisig_enrollment_handler.go`)
 * sends no error codes: each response carries the Go sentinel's text as
 * `error`, either verbatim or wrapped (`"owner2: user … does not hold the
 * Owner role"`), and the `challenge.Err*` family arrives as a validation error
 * whose text sits in `extras.signed_challenge_xdr`. So we match on the
 * sentinel text, which is stable, and fall back to the HTTP status.
 *
 * Keep `ENROLLMENT_ERROR_RULES` in sync with the sentinels in
 * `internal/services/multisig_enrollment_service.go`,
 * `internal/multisig/challenge/verifier.go` and
 * `internal/data/{models,multisig_enrollment_session}.go`.
 */

export type EnrollmentErrorCode =
  // services.Err*
  | "NOT_BACKEND_ONLY"
  | "UNEXPECTED_SIGNERS"
  | "SAME_OWNER"
  | "NOT_PARTICIPANT"
  | "SESSION_EXPIRED"
  | "OWNER1_REPROOF_REQUIRED"
  | "OWNER2_REPROOF_REQUIRED"
  | "NOT_AWAITING_OWNER_KEY"
  // challenge.Err*
  | "CHALLENGE_NOT_FOUND"
  | "CHALLENGE_CONSUMED"
  | "CHALLENGE_EXPIRED"
  | "CHALLENGE_MISMATCH"
  | "CHALLENGE_INVALID"
  | "CHALLENGE_SIGNATURE_COUNT"
  | "CHALLENGE_SIGNATURE"
  // data.Err*
  | "ALREADY_EXISTS"
  | "INVALID_TRANSITION"
  | "DUPLICATE_OWNER_KEY"
  | "NOT_FOUND"
  // request validation
  | "INVALID_PUBLIC_KEY"
  | "MISSING_OWNER2"
  | "MISSING_SIGNED_CHALLENGE"
  // role checks (403)
  | "OWNER2_NOT_OWNER"
  | "NOT_OWNER"
  | "FORBIDDEN"
  // transport
  | "UNAUTHORIZED"
  | "UNKNOWN";

type Rule = { code: EnrollmentErrorCode; match: string; message: string };

/** Ordered: the first rule whose `match` text appears in the error wins. */
export const ENROLLMENT_ERROR_RULES: ReadonlyArray<Rule> = [
  {
    code: "NOT_BACKEND_ONLY",
    match: "organization is not in the backend-only state",
    message:
      "Your organization already uses co-signing, so it can't be set up again. Manage your co-signers in Team members.",
  },
  {
    code: "UNEXPECTED_SIGNERS",
    match: "treasury account has unexpected on-chain signers",
    message:
      "The account being enrolled already has other signers on the Stellar network. That has to be resolved before co-signing can be set up, so please contact support.",
  },
  {
    code: "SAME_OWNER",
    match: "initiator and owner2 must be different users",
    message: "Choose a different Owner. Co-signing needs two different people.",
  },
  {
    code: "NOT_PARTICIPANT",
    match: "user is not a participant in this enrollment session",
    message: "Only the two Owners taking part in this co-signing setup can do this.",
  },
  {
    code: "SESSION_EXPIRED",
    match: "multisig enrollment session has expired",
    message:
      "This co-signing setup expired. Setups and invitations last 4 days, and the keys collected so far were cleared. Start a new one.",
  },
  {
    code: "OWNER1_REPROOF_REQUIRED",
    match: "owner 1's key ownership proof is stale",
    message:
      "Owner 1 shared their key more than 24 hours ago, so they need to prove they still control it in VIA Wallet before setup can continue.",
  },
  {
    code: "OWNER2_REPROOF_REQUIRED",
    match: "owner 2's key ownership proof is stale",
    message:
      "Owner 2 shared their key more than 24 hours ago, so they need to prove they still control it in VIA Wallet before setup can continue.",
  },
  {
    code: "NOT_AWAITING_OWNER_KEY",
    match: "session is not currently awaiting an owner key from this user",
    message: "This co-signing setup isn't waiting for your key right now.",
  },
  {
    code: "CHALLENGE_NOT_FOUND",
    match: "key ownership challenge not found",
    message: "We couldn't find your key check. Connect VIA Wallet and try again.",
  },
  {
    code: "CHALLENGE_CONSUMED",
    match: "key ownership challenge already consumed",
    message: "That key check was already used. Each one works only once, so start a new one.",
  },
  {
    code: "CHALLENGE_EXPIRED",
    match: "key ownership challenge expired",
    message: "Your key check expired. Each one lasts 5 minutes, so sign the new one in VIA Wallet.",
  },
  {
    code: "CHALLENGE_MISMATCH",
    match: "signed challenge does not match stored challenge",
    message:
      "What VIA Wallet signed doesn't match the key check we sent. Start a new key check and try again.",
  },
  {
    code: "CHALLENGE_SIGNATURE_COUNT",
    match: "signed challenge must contain exactly one signature",
    message: "The key check has to be signed by one key only. Sign it again in VIA Wallet.",
  },
  {
    code: "CHALLENGE_SIGNATURE",
    match: "key ownership challenge signature is invalid",
    message:
      "The signature doesn't match the key you chose. In VIA Wallet, sign with that same key and try again.",
  },
  {
    code: "CHALLENGE_INVALID",
    match: "signed challenge is invalid",
    message: "We couldn't read what VIA Wallet signed. Start a new key check and try again.",
  },
  {
    code: "DUPLICATE_OWNER_KEY",
    match: "enrollment owners must use different stellar public keys",
    message: "Each Owner has to use a different key. Choose another key in VIA Wallet.",
  },
  {
    code: "INVALID_TRANSITION",
    match: "invalid multisig enrollment session status transition",
    message: "This co-signing setup has already moved on. Refresh the page to see where it stands.",
  },
  {
    code: "ALREADY_EXISTS",
    match: "record already exists",
    message:
      "A co-signing setup is already in progress. Only one can run at a time, so finish or cancel that one first.",
  },
  {
    code: "INVALID_PUBLIC_KEY",
    match: "public_key must be a valid stellar g-address",
    message: "That isn't a valid Stellar public key.",
  },
  {
    code: "MISSING_OWNER2",
    match: "owner2_user_id is required",
    message: "Choose the Owner who will co-sign with you.",
  },
  {
    code: "MISSING_SIGNED_CHALLENGE",
    match: "signed_challenge_xdr is required",
    message: "Sign the key check in VIA Wallet first.",
  },
  {
    code: "OWNER2_NOT_OWNER",
    match: "owner2: user",
    message: "The person you chose needs the Owner role before they can co-sign.",
  },
  {
    code: "NOT_OWNER",
    match: "does not hold the owner role",
    message: "Only Owners can set up or cancel co-signing.",
  },
];

const STATUS_FALLBACKS: ReadonlyArray<{
  status: number;
  code: EnrollmentErrorCode;
  message: string;
}> = [
  { status: 401, code: "UNAUTHORIZED", message: "Your session has ended. Sign in again." },
  {
    status: 403,
    code: "FORBIDDEN",
    message: "You don't have permission to manage co-signing. Only Owners do.",
  },
  { status: 404, code: "NOT_FOUND", message: "There's no co-signing setup in progress." },
  {
    status: 409,
    code: "INVALID_TRANSITION",
    message: "This co-signing setup has already moved on. Refresh the page to see where it stands.",
  },
];

export const ENROLLMENT_GENERIC_ERROR_MESSAGE =
  "Something went wrong with co-signing setup. Try again.";

export class EnrollmentError extends Error {
  readonly code: EnrollmentErrorCode;
  readonly status?: number;
  /** The backend's original text, kept for logs; never render it. */
  readonly rawMessage: string;

  constructor(code: EnrollmentErrorCode, message: string, rawMessage: string, status?: number) {
    super(message);
    this.name = "EnrollmentError";
    this.code = code;
    this.status = status;
    this.rawMessage = rawMessage;
  }
}

/** Every string the backend put on the error: `error` plus `extras` values. */
const backendText = (err: Partial<AppError>): string => {
  const parts = [err.message ?? ""];
  for (const value of Object.values(err.extras ?? {})) {
    if (typeof value === "string") parts.push(value);
  }
  return parts.join(" | ");
};

export const mapEnrollmentError = (err: unknown): EnrollmentError => {
  if (err instanceof EnrollmentError) return err;

  const appError: Partial<AppError> =
    err && typeof err === "object" ? (err as Partial<AppError>) : { message: String(err ?? "") };
  const raw = backendText(appError);
  const haystack = raw.toLowerCase();
  const status = appError.status;

  const rule = ENROLLMENT_ERROR_RULES.find((r) => haystack.includes(r.match));
  if (rule) return new EnrollmentError(rule.code, rule.message, raw, status);

  const fallback = STATUS_FALLBACKS.find((f) => f.status === status);
  if (fallback) return new EnrollmentError(fallback.code, fallback.message, raw, status);

  return new EnrollmentError("UNKNOWN", ENROLLMENT_GENERIC_ERROR_MESSAGE, raw, status);
};

export const getEnrollmentErrorMessage = (err: unknown): string => mapEnrollmentError(err).message;
